// @ts-check
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSessionJWT } from "utils";
import { signTiliaTOS } from "api/accounts";
import { isSignedTOS, tosQueryKey } from "./index.js";

/**
 * useSignTOS
 * @returns the `POST /v1/user-info/tos/tilia` mutation. It resolves with
 * `{ posted, response }`: `posted` is false when the cache short-circuit fired, so callers that
 * only care about consent actually being recorded - the analytics in `TOSProvider` - can tell a
 * real signature from a no-op. Explicit rather than "resolved with undefined", since the POST's
 * own body is not ours to make meaningful.
 * @description Signing is idempotent against the query cache, which is what lets callers
 * `await signTOS()` unconditionally without first checking whether consent is needed.
 */
export default function useSignTOS() {
  // useQueryClient(), not the api/queryClient module singleton: tests build a fresh client per
  // render, and a write to the singleton would land in a cache nobody observes.
  const queryClient = useQueryClient();
  const { sessionJWT, sessionData } = useSessionJWT();
  const queryKey = tosQueryKey(sessionData?.account_id);

  return useMutation({
    mutationFn: async () => {
      // The already-signed short-circuit reads the CACHE, so one sign is visible to every
      // instance, including ones that mount later.
      if (isSignedTOS(queryClient.getQueryData(queryKey))) return { posted: false };

      const response = await signTiliaTOS(sessionJWT);

      // Cancel before writing: setQueryData has no revision check, so a GET still in flight would
      // resolve after our write and clobber it with its pre-POST snapshot (signed_tos: false),
      // re-prompting a user who has already signed.
      await queryClient.cancelQueries({ queryKey });

      // Write here rather than in onSuccess: react-query skips a component-level onSuccess if the
      // component unmounts before the mutation settles, and with staleTime: Infinity the cache
      // would then hold "not signed" forever. The Checkout -> /addpm route switch unmounts, so
      // that is reachable. setQueryData, not invalidateQueries — zero refetch. Merge, don't
      // replace.
      queryClient.setQueryData(queryKey, (old) => ({
        ...old,
        payload: { ...old?.payload, signed_tos: true },
      }));

      return { posted: true, response };
    },
  });
}
